import type { Task } from "../tasks/types.js";
import { occurrencesBetween, seriesEndsBefore } from "./expand.js";
import { applyOverrides, effectiveBoundary, resolveEffectiveRrule } from "./overrides/compose.js";
import type { RecurrenceOverride } from "./overrides/types.js";
import type { RecurrenceRule } from "./types.js";

/**
 * Return non-recurring tasks plus virtual occurrences synthesized from each
 * open recurring task's rule for the [from, to] window.
 *
 * Recurring source rows are templates only — they never appear in the output.
 * Every calendar tile for a series is a synthetic virtual flagged with
 * `virtual: true` and `parent_id` pointing at the source. Virtuals are never
 * persisted.
 *
 * Override semantics (RFC 5545 RECURRENCE-ID + EXCEPTION):
 *   - thisandfuture overrides whose ref <= occurrence layer their fields
 *     (latest non-null wins).
 *   - the single override at ref === occurrence wins over thisandfuture for
 *     its specific occurrence.
 *   - status='cancelled' suppresses the occurrence (EXDATE-equivalent).
 *   - a thisandfuture override may carry a new `rrule` that changes the
 *     schedule shape from its ref onward (until a later thisandfuture
 *     supersedes it).
 */
export function expandTasksForCalendar(
  tasks: ReadonlyArray<Task>,
  rules: Record<string, RecurrenceRule>,
  from: Date,
  to: Date,
  overridesByRuleId: Record<string, ReadonlyArray<RecurrenceOverride>> = {},
): Task[] {
  const out: Task[] = [];
  for (const t of tasks) {
    if (t.deleted_at) continue;
    if (!t.recurrence_id) {
      out.push(t);
      continue;
    }
    if (t.status !== "open") continue;
    const rule = rules[t.recurrence_id];
    if (!rule) continue;
    out.push(...expandRecurringTask(t, rule, overridesByRuleId[rule.id] ?? [], from, to));
  }
  return out;
}

/**
 * Synthesize the virtual occurrences of ONE recurring task within [from, to].
 * Pure and deterministic in its inputs — the same `(task, rule, overrides, from,
 * to)` always yields equal output, so callers may memoize on those references
 * (the calendar window caches per-rule so an edit elsewhere reuses untouched
 * series). The source `task` is part of the input precisely because virtuals
 * inherit its fields (title/color/…), so an "all" edit must invalidate the cache.
 */
export function expandRecurringTask(
  task: Task,
  rule: RecurrenceRule,
  overrides: ReadonlyArray<RecurrenceOverride>,
  from: Date,
  to: Date,
): Task[] {
  const out: Task[] = [];
  for (const o of expandSegments(rule, overrides, from, to)) {
    const iso = o.toISOString();
    const effective = applyOverrides(task, iso, overrides);
    if (!effective) continue;
    out.push({
      ...task,
      id: `${task.id}::v::${iso}`,
      due_at: effective.due_at,
      duration_minutes: effective.duration_minutes,
      title: effective.title,
      notes: effective.notes,
      priority: effective.priority,
      custom_color: effective.custom_color,
      all_day: effective.all_day,
      status: effective.status,
      snoozed_until: effective.snoozed_until,
      tz: rule.tz,
      parent_id: task.id,
      virtual: true,
    });
  }
  return out;
}

/**
 * Expand occurrences across thisandfuture-rrule boundaries. Each segment uses
 * the rrule in effect for that range; we synthesise a per-segment rule view
 * with the segment's rrule + dtstart so `occurrencesBetween` honours it.
 */
function expandSegments(
  rule: RecurrenceRule,
  overrides: ReadonlyArray<RecurrenceOverride>,
  from: Date,
  to: Date,
): Date[] {
  // The base rule's `until` caps every segment, so an ended series can be
  // dropped before scanning its overrides and resolving per-segment rrules.
  if (seriesEndsBefore(rule, from)) return [];
  // Every thisandfuture override that affects the schedule shape OR the
  // segment anchor (due_at) becomes a segment boundary. The boundary's lower
  // bound for expansion is `effectiveBoundary(o)` — when the user moved the
  // tile EARLIER (due_at < ref), the new schedule kicks in at due_at so the
  // moved tile still appears in expansion.
  const boundaries = overrides
    .filter((o) => o.range === "thisandfuture" && (o.rrule || o.due_at) && new Date(effectiveBoundary(o)) <= to)
    .slice()
    .sort((a, b) => effectiveBoundary(a).localeCompare(effectiveBoundary(b)));

  if (boundaries.length === 0) {
    return occurrencesBetween(rule, from, to);
  }

  const segments: Array<{ start: Date; end: Date; rrule: string; dtstart: string }> = [];
  const firstBoundary = new Date(effectiveBoundary(boundaries[0]!));
  if (from < firstBoundary) {
    segments.push({ start: from, end: new Date(firstBoundary.getTime() - 1), rrule: rule.rrule, dtstart: rule.dtstart });
  }
  for (let i = 0; i < boundaries.length; i++) {
    const b = boundaries[i]!;
    const segStart = new Date(effectiveBoundary(b));
    const nextB = boundaries[i + 1];
    const segEnd = nextB ? new Date(new Date(effectiveBoundary(nextB)).getTime() - 1) : to;
    const effRrule = resolveEffectiveRrule(rule, effectiveBoundary(b), overrides);
    // The override's `due_at` is the new anchor for the reshaped schedule —
    // use it as the segment's dtstart so e.g. FREQ=WEEKLY;BYDAY=TU emits
    // Tuesdays starting on the right week.
    segments.push({
      start: segStart < from ? from : segStart,
      end: segEnd > to ? to : segEnd,
      rrule: effRrule,
      dtstart: b.due_at ?? b.recurrence_id_ref,
    });
  }

  const out: Date[] = [];
  for (const seg of segments) {
    if (seg.start > seg.end) continue;
    const view: RecurrenceRule = { ...rule, rrule: seg.rrule, dtstart: seg.dtstart };
    out.push(...occurrencesBetween(view, seg.start, seg.end));
  }
  return out;
}
